import React, { useState, useEffect, useRef } from 'react';
import orbieeAvatar from '../images/Orbiee_Avatra.png';

// Context-specific loading messages
// Intent names match backend LLM classification (see orchestrator.py)
export const LOADING_MESSAGES_BY_CONTEXT = {
  // General/Default messages (used for "uncertain" intent)
  default: [
    "Let me check that for you...",
    "Almost there...",
    "Just a moment...",
  ],
  
  // Shopping for self
  self: [
    "Searching our collection...",
    "Finding the perfect match...",
    "Browsing through products...",
    "Looking for what you need...",
    "Discovering amazing items...",
    "Almost there...",
  ],
  
  // Shopping for others
  others: [
    "Searching our collection...",
    "Finding the perfect match...",
    "Browsing through products...",
    "Looking for what you need...",
    "Discovering amazing items...",
    "Almost there...",
  ],
  
  // Product detail page
  detail_page: [
    "Fetching product details...",
    "Gathering information...",
    "Loading specifications...",
    "Getting all the details...",
    "Almost ready...",
    "Just a moment...",
  ],
  
  // Add to cart
  add_to_cart: [
    "Adding to your cart...",
    "Preparing your item...",
    "Almost done...",
    "Adding it now...",
  ],
  
  // View cart
  view_cart: [
    "Loading your cart...",
    "Fetching cart items...",
    "Almost there...",
    "Just a moment...",
  ],
  
  // Complete purchase (showing address and payment details)
  complete_purchase: [
    "Loading your details...",
    "Preparing checkout...",
    "Getting your information...",
    "Almost ready...",
    "Setting up payment...",
    "Just a moment...",
  ],
  
  // Confirm purchase
  confirm_purchase: [
    "Processing your payment...",
    "Confirming your order...",
    "Finalizing purchase...",
    "Almost there...",
    "Completing transaction...",
    "Just a moment...",
  ],
  
  // Uncertain intent (fallback to default)
  uncertain: [
    "Let me check that for you...",
    "Almost there...",
    "Just a moment...",
  ],
};

/**
 * Intent-Based Loading Component
 * 
 * Displays context-aware loading messages based on backend LLM intent classification.
 * Messages are dynamically selected based on the shopping intent (self, others, detail_page, etc.)
 * to provide relevant feedback to users while waiting for API responses.
 * 
 * @param {boolean} isLoading - Whether to show the loading animation
 * @param {string} context - Intent from backend LLM classification (see orchestrator.py)
 *                          Options: 'default', 'self', 'others', 'detail_page', 'add_to_cart', 
 *                                   'view_cart', 'complete_purchase', 'confirm_purchase', 'uncertain'
 */
const IntentBasedLoading = ({ isLoading, context = 'default' }) => {
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingMessageIntervalRef = useRef(null);
  
  // Map context to messages, use default for uncertain or unknown intents
  const getMessagesForContext = (ctx) => {
    if (ctx === 'uncertain' || !LOADING_MESSAGES_BY_CONTEXT[ctx]) {
      return LOADING_MESSAGES_BY_CONTEXT.default;
    }
    return LOADING_MESSAGES_BY_CONTEXT[ctx] || LOADING_MESSAGES_BY_CONTEXT.default;
  };
  
  const messagesRef = useRef(getMessagesForContext(context));

  // Update messages when context changes
  useEffect(() => {
    const newMessages = getMessagesForContext(context);
    messagesRef.current = newMessages;
    
    if (isLoading && loadingMessageIntervalRef.current) {
      // Immediately update to new messages when context changes during loading
      setLoadingMessage(newMessages[0]);
      setLoadingMessageIndex(0);
      
      // Clear existing interval and restart with new messages
      if (loadingMessageIntervalRef.current) {
        clearInterval(loadingMessageIntervalRef.current);
      }
      
      // Restart interval with new messages
      loadingMessageIntervalRef.current = setInterval(() => {
        setLoadingMessageIndex((prev) => {
          const next = (prev + 1) % newMessages.length;
          setLoadingMessage(newMessages[next]);
          return next;
        });
      }, 1500);
    }
  }, [context, isLoading]);

  // Start animated loading messages
  const startLoadingAnimation = () => {
    const messages = messagesRef.current;
    setLoadingMessage(messages[0]);
    setLoadingMessageIndex(0);
    
    // Rotate through messages every 1.5 seconds
    loadingMessageIntervalRef.current = setInterval(() => {
      setLoadingMessageIndex((prev) => {
        const next = (prev + 1) % messages.length;
        setLoadingMessage(messages[next]);
        return next;
      });
    }, 1500);
  };
  
  // Stop animated loading messages
  const stopLoadingAnimation = () => {
    if (loadingMessageIntervalRef.current) {
      clearInterval(loadingMessageIntervalRef.current);
      loadingMessageIntervalRef.current = null;
    }
    setLoadingMessage("");
    setLoadingMessageIndex(0);
  };

  // Start/stop animation based on isLoading prop
  useEffect(() => {
    if (isLoading) {
      startLoadingAnimation();
    } else {
      stopLoadingAnimation();
    }

    // Cleanup on unmount
    return () => {
      stopLoadingAnimation();
    };
  }, [isLoading]);

  if (!isLoading) {
    return null;
  }

  return (
    <div className="flex gap-3 mb-4 animate-fadeIn">
      {/* Animated Assistant Avatar */}
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full overflow-hidden shadow-lg shadow-indigo-200/50 ring-2 ring-indigo-200 animate-pulse bg-white">
          <img
            src={orbieeAvatar}
            alt="Orbiee assistant"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Floating animation rings */}
        <div className="absolute inset-0 rounded-full border-2 border-indigo-300 animate-ping opacity-20" />
        <div 
          className="absolute inset-0 rounded-full border-2 border-purple-300 animate-ping opacity-20" 
          style={{ animationDelay: '0.5s' }} 
        />
      </div>
      
      {/* Message Bubble */}
      <div className="bg-white rounded-2xl px-6 py-4 shadow-lg border border-gray-100 flex-1 relative overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 opacity-50 animate-shimmer" />
        
        <div className="relative z-10">
          {/* Rotating message text */}
          <div className="flex items-center gap-3">
            <p 
              className="text-gray-700 text-base font-medium animate-fadeInMessage"
              key={loadingMessage}
              style={{
                fontFamily: "'Cormorant Garamond', 'Playfair Display', 'Georgia', serif",
                letterSpacing: '0.02em'
              }}
            >
              {loadingMessage}
            </p>
            
            {/* Animated typing dots */}
            <div className="flex gap-1 items-center">
              <span 
                className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" 
                style={{ animationDelay: '0ms' }} 
              />
              <span 
                className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" 
                style={{ animationDelay: '150ms' }} 
              />
              <span 
                className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" 
                style={{ animationDelay: '300ms' }} 
              />
            </div>
          </div>
        </div>
        
        {/* CSS Animations */}
        <style>{`
          @keyframes fadeInMessage {
            from {
              opacity: 0;
              transform: translateY(4px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes shimmer {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }
          .animate-fadeInMessage {
            animation: fadeInMessage 0.4s ease-out;
          }
          .animate-shimmer {
            background-size: 200% 200%;
            animation: shimmer 3s ease-in-out infinite;
          }
        `}</style>
      </div>
    </div>
  );
};

export default IntentBasedLoading;

